import React, { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { usePreloadedQuery } from 'react-relay';
import type { PreloadedQuery } from 'react-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';

import type { usePerformanceInsightsAccessQuery } from '../hooks/__generated__/usePerformanceInsightsAccessQuery.graphql.ts';
import {
	performanceInsightsAccessQuery,
	usePerformanceInsightsAccess,
} from '../hooks/usePerformanceInsightsAccess.ts';

const renderNothing = () => null;
export type PerformanceInsightsAccessProps = {
	children: ReactNode;
	denied?: ReactNode;
	loading?: ReactNode;
	renderUnavailable?: (onRetry: () => void) => ReactNode;
	interactionName: string;
};

const EntitlementReader = ({
	queryRef,
	children,
	denied,
	renderUnavailable,
	onRetry,
}: {
	queryRef: PreloadedQuery<usePerformanceInsightsAccessQuery>;
	children: ReactNode;
	denied: ReactNode;
	renderUnavailable: (onRetry: () => void) => ReactNode;
	onRetry: () => void;
}): React.JSX.Element => {
	const data = usePreloadedQuery(performanceInsightsAccessQuery, queryRef);
	const perfIsEntitled = data.entitlements?.performanceInsights?.isEntitled;
	const error = useMemo(
		() =>
			typeof perfIsEntitled === 'boolean'
				? undefined
				: new Error('Performance Insights entitlement metadata is unavailable'),
		[perfIsEntitled],
	);
	if (error) return <ErrorDisplay error={error}>{renderUnavailable(onRetry)}</ErrorDisplay>;
	return <>{perfIsEntitled === true ? children : denied}</>;
};

export const PerformanceInsightsAccess = ({
	children,
	denied = null,
	loading = null,
	renderUnavailable = renderNothing,
	interactionName,
}: PerformanceInsightsAccessProps): React.JSX.Element => {
	const { queryRef, error, isInitialLoading, retryVersion, retry } = usePerformanceInsightsAccess();
	const renderError = useCallback(
		() => <>{renderUnavailable(retry)}</>,
		[renderUnavailable, retry],
	);
	if (isInitialLoading || (!queryRef && !error)) return <>{loading}</>;
	if (error) return <ErrorDisplay error={error}>{renderUnavailable(retry)}</ErrorDisplay>;
	return (
		<GenericErrorBoundary
			key={retryVersion}
			attribution={Attribution.INHERIT}
			renderOnError={renderError}
		>
			<RelaySuspense interactionName={interactionName} fallback={loading}>
				{queryRef ? (
					<EntitlementReader
						queryRef={queryRef}
						denied={denied}
						renderUnavailable={renderUnavailable}
						onRetry={retry}
					>
						{children}
					</EntitlementReader>
				) : (
					loading
				)}
			</RelaySuspense>
		</GenericErrorBoundary>
	);
};
